if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('.tms-add-to-cart__text') || this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const originalLabel = this.submitButtonText ? this.submitButtonText.textContent : null;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Set when the add is rejected (e.g. only N in stock), so the button never flashes "Added".
        let addFailed = false;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              addFailed = true;
              // A 422 can still add part of the quantity ("Only 5 items were added…"), so bring the
              // cart count and drawer in line with the real cart.
              this.refreshCart();

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            // Success micro-feedback: briefly show "Added", then restore the original label.
            if (!this.error && !addFailed && originalLabel != null) {
              const addedLabel = window.variantStrings?.added || 'Added';
              this.submitButton.classList.add('tms-btn-added');
              this.submitButtonText.textContent = addedLabel;
              clearTimeout(this._addedTimer);
              this._addedTimer = setTimeout(() => {
                this.submitButton.classList.remove('tms-btn-added');
                if (this.submitButtonText.textContent === addedLabel) {
                  this.submitButtonText.textContent = originalLabel;
                }
              }, 1600);
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      refreshCart() {
        if (!this.cart || typeof this.cart.getSectionInnerHTML !== 'function') return;
        const sections = this.cart.getSectionsToRender();
        const ids = [...new Set(sections.map((section) => section.section || section.id))];
        fetch(`${routes.cart_url}?sections=${ids.join(',')}`)
          .then((response) => response.json())
          .then((html) => {
            sections.forEach((section) => {
              const markup = html[section.section || section.id];
              const target = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              if (!markup || !target) return;
              target.innerHTML = this.cart.getSectionInnerHTML(markup, section.selector);
            });
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            // The drawer's overlay was replaced, so re-attach close-on-click as renderContents does.
            const overlay = this.cart.querySelector('#CartDrawer-Overlay');
            if (overlay && typeof this.cart.close === 'function') {
              overlay.addEventListener('click', this.cart.close.bind(this.cart));
            }
          })
          .catch((e) => console.error(e));
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text, enabledText) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          // Preserve the custom CTA label (e.g. "Special Order Now", "Save X%") carried in the
          // re-fetched section HTML instead of always reverting to the generic "Add to cart".
          this.submitButtonText.textContent = enabledText || window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
